
import { FormEvent, useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'

export function Login() {
  const navigate = useNavigate()
  const location = useLocation()
  const params = new URLSearchParams(location.search)

  const [email, setEmail] = useState(params.get('email') || '')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)


  async function routeSignedInUser(userId: string, fallbackCode?: string) {
    if (!supabase) return

    const { data: membership } = await supabase
      .from('memberships')
      .select('role')
      .eq('user_id', userId)
      .order('created_at', { ascending: true })
      .limit(1)
      .maybeSingle()

    if (membership?.role) {
      navigate(membership.role === 'member' ? '/employee' : '/app', { replace: true })
      return
    }

    const { data: { user } } = await supabase.auth.getUser()
    const storedCode = String(user?.user_metadata?.employee_join_code ?? '')
    const code = (fallbackCode || storedCode).trim()

    if (code) {
      const { error: joinError } = await supabase.rpc('join_organisation_by_code', {
        p_code: code,
        p_display_name: String(user?.user_metadata?.full_name ?? '').trim() || null
      })

      if (joinError) throw joinError
      navigate('/employee', { replace: true })
      return
    }

    navigate('/onboarding', { replace: true })
  }

  async function submit(event: FormEvent) {
    event.preventDefault()
    if (!supabase) {
      setError('Supabase is not configured.')
      return
    }

    setBusy(true)
    setError('')

    const { data, error: signInError } = await supabase.auth.signInWithPassword({ email, password })

    if (signInError || !data.user) {
      setBusy(false)
      setError(signInError?.message || 'Unable to sign in.')
      return
    }

    try {
      const legacyWorkspaceCode = params.get('employeeCode')?.trim() || ''
      if (legacyWorkspaceCode) {
        const { error: joinError } = await supabase.rpc('join_organisation_by_code', {
          p_code: legacyWorkspaceCode,
          p_display_name: String(data.user.user_metadata?.full_name ?? '').trim() || null
        })

        if (joinError) throw joinError
        navigate('/employee', { replace: true })
        return
      }

      const explicitReturn = params.get('returnTo')
      if (explicitReturn) {
        navigate(explicitReturn, { replace: true })
        return
      }

      await routeSignedInUser(data.user.id, params.get('employeeCode') || undefined)
    } catch (err) {
      setBusy(false)
      setError(err instanceof Error ? err.message : 'Could not enter the workspace.')
    }
  }

  return <div className="auth-page">
    <div className="auth-brand"><img className="mail-percent-logo app-logo" src="/images/brand/mail-percent-logo.png" alt="Mail Percent" /></div>
    <main className="auth-card">
      <span className="eyebrow">Welcome back</span>
      <h1>Sign in to Mail Percent</h1>
      <p>
        {params.get('employee') === '1'
          ? 'Sign in with the same work email you used on your organisation invitation. Mail Percent will return you to that invitation automatically.'
          : 'Managers enter their workspace normally. Employees should use the invitation link shared by their manager the first time they join.'}
      </p>

      <form onSubmit={submit} className="auth-form">
        <label>Email<input className="search-input" type="email" value={email} onChange={e => setEmail(e.target.value)} required autoComplete="email" /></label>

        <label>Password
          <div className="password-field">
            <input className="search-input" type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} required autoComplete="current-password" />
            <button type="button" className="password-toggle" onClick={() => setShowPassword(value => !value)} aria-label={showPassword ? 'Hide password' : 'Show password'}>
              {showPassword ? <Eye size={18}/> : <EyeOff size={18}/>}
            </button>
          </div>
        </label>


        {error && <p className="auth-error">{error}</p>}
        <button className="button primary large" disabled={busy}>{busy ? <span className="blue-spinner button-spinner"/> : 'Sign in'}</button>
      </form>

      <p className="auth-switch">New to Mail Percent? <Link to={params.get('returnTo') ? `/signup?mode=employee&email=${encodeURIComponent(email)}&returnTo=${encodeURIComponent(params.get('returnTo') || '')}` : '/signup'}>Create an account</Link></p>
    </main>
  </div>
}
